import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from '../axios';
import { Country } from "../types";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const CountriesPage: React.FC = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<Country[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<Country | null>(null);
    //const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortCountriesList";
    const savedFilterKey = "filterCountriesList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/Countries`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        //const payload = { name };

        try {
            if (selectedItem?.id == null || selectedItem.id === '') {
                const payload: Country = {
                    ...selectedItem, id: crypto.randomUUID(), name: selectedItem?.name ?? "",
                };

                //delete payload.id; // Remove the id field
                await axiosExt.post(`${API_BASE_URL}/Countries`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axiosExt.put(`${API_BASE_URL}/Countries/${selectedItem.id}`, selectedItem, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
        }
        catch (err) {
            console.log(err);
        }
        

        setOpen(false);
        fetchItems();
    };

    const handleSyncCountries = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            await axiosExt.post(`${API_BASE_URL}/countries/syncwithaccounting`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchItems();
        } catch (error) {
            console.error("Error syncing paid invoices:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/Countries/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };



    const handleEdit = (item: Country) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 200 },
        { field: "accountingId", headerName: t("Accounting Id"), width: 200 },
        { field: "region", headerName: t("Region"), width: 150 },
        { field: "isOwnCountry", headerName: t("Is Own Country"), width: 200 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                {/* Header section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <h2 >{t("Countries")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                        onClick={() => {
                            setSelectedItem({ id: "", name: "" });
                            setOpen(true);
                        }}
                    >
                        {t("Add New")}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => handleSyncCountries()}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                        disabled={loading}
                    >
                        {t("Reload from accounting")}
                    </Button>
                </Box>

                {/* Grid section */}
                <Box
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        height: "calc(100vh - 240px)",
                        "& .MuiDataGrid-root": {
                            border: "none",
                        },
                    }}
                >
                    <DataGrid
                        rows={items}
                        columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        getRowId={(r) => r.id}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[50, 100, 200]}
                    />
                </Box>

                {/* Add/Edit dialog */}
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedItem ? t("Edit") : t("Add")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={t("Name")}
                            fullWidth
                            value={selectedItem?.name}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, name: e.target.value } : null
                                )
                            }
                            margin="dense"
                        />
                        <TextField
                            label={t("Accounting Id")}
                            fullWidth
                            value={selectedItem?.accountingId}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, accountingId: e.target.value } : null
                                )
                            }
                            margin="dense"
                        />
                        <TextField
                            label={t("Region")}
                            fullWidth
                            value={selectedItem?.region}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, region: e.target.value } : null
                                )
                            }
                            margin="dense"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedItem?.isOwnCountry ?? false}
                                    onChange={(e) =>
                                        setSelectedItem((prev) =>
                                            prev ? { ...prev, isOwnCountry: e.target.checked } : null
                                        )
                                    }
                                />
                            }
                            label={t("Is Own Country")}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                        <Button onClick={handleSave}>{t("Save")}</Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Delete Dialog */}
                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Delete")}</DialogTitle>
                    <DialogContent>
                        {t("Are you sure you want to delete this item?")}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete}>{t("Cancel")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">
                            {t("Delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LayoutAdmin>
    );

};

export default CountriesPage;
